import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { pageColors } from "../mock";
import { uploadFile } from "../lib/uploads";
import {
  ArrowLeft,
  Upload,
  X,
  Check,
  Music as MusicIcon,
  Palette,
  ShoppingBag,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CONFIG = {
  music: {
    title: "Apply to EZ Money Records",
    lead: "Tell us who you are and drop your best sample. Demos help — mp3, wav, or a short video work.",
    icon: MusicIcon,
    color: pageColors.music,
    accept: "image/*,audio/*,video/*",
    labels: {
      display_name: "Artist / stage name",
      tagline: "One-line hook (optional)",
      pitch: "Tell us about you — genre, background, what you're working on.",
    },
  },
  brand: {
    title: "Apply to be hosted",
    lead: "Bring your brand into the fold. Show us hero shots, product photos, and where you're at.",
    icon: ShoppingBag,
    color: pageColors.fashion,
    accept: "image/*",
    labels: {
      display_name: "Brand name",
      tagline: "Brand tagline (optional)",
      pitch: "Tell us the story — what you make, who you make it for, why it belongs here.",
    },
  },
  art: {
    title: "Apply for the Art Show",
    lead: "Share a couple of your favourite pieces and tell us what you'd bring.",
    icon: Palette,
    color: pageColors.art,
    accept: "image/*,video/*",
    labels: {
      display_name: "Artist name",
      tagline: "One-line hook (optional)",
      pitch: "Medium, themes, and the piece(s) you'd bring.",
    },
  },
};

const Apply = () => {
  const { kind } = useParams();
  const cfg = CONFIG[kind];
  const navigate = useNavigate();
  const { current, token } = useAuth();

  const [form, setForm] = useState({
    display_name: "",
    contact_email: current?.email || "",
    tagline: "",
    pitch: "",
    website: "",
    instagram: current?.instagram || "",
    socials: "",
    location: current?.location || "",
  });
  const [hero, setHero] = useState(null); // { url, path, kind }
  const [media, setMedia] = useState([]); // list of uploaded refs
  const [busy, setBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const Icon = cfg?.icon;

  useEffect(() => {
    if (cfg) document.title = `TMM|${cfg.title.toUpperCase()}`;
  }, [cfg]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const gated = useMemo(() => {
    if (!current) return { reason: "You need to sign in first.", cta: "/members" };
    if (current.status !== "approved") return { reason: "Only approved members can apply — you're still pending.", cta: "/members" };
    return null;
  }, [current]);

  if (!cfg) {
    return (
      <>
        <Header hoverColor={pageColors.contact} compact={false} />
        <div className="apply-page">
          <button className="feed-back" onClick={() => navigate(-1)}><ArrowLeft size={16}/> Back</button>
          <h1 className="apply-title">Unknown application type.</h1>
        </div>
      </>
    );
  }

  const onFile = async (e, kindHint = "media") => {
    setUploadErr("");
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const uploaded = await uploadFile(f, token);
        if (kindHint === "hero") setHero(uploaded);
        else setMedia((m) => [...m, uploaded]);
      }
    } catch (ex) {
      setUploadErr(ex.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removeMedia = (idx) => setMedia((m) => m.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.display_name.trim() || !form.contact_email.trim() || !form.pitch.trim()) {
      setErr("Name, email, and pitch are required.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        kind,
        display_name: form.display_name.trim(),
        contact_email: form.contact_email.trim(),
        tagline: form.tagline,
        pitch: form.pitch,
        website: form.website,
        instagram: form.instagram,
        socials: form.socials,
        location: form.location,
        hero_image_url: hero?.url || null,
        media: media.map((m) => ({ url: m.url, path: m.path, kind: m.kind, label: m.original_filename || "" })),
      };
      const res = await fetch(`${API}/api/applications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      setSubmitted(true);
    } catch (ex) {
      setErr(ex.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header hoverColor={cfg.color} compact={false} />
      <div className="apply-page" data-testid={`apply-page-${kind}`}>
        <button className="feed-back" onClick={() => navigate(-1)} data-testid="apply-back">
          <ArrowLeft size={16} /> Back
        </button>

        <header className="apply-hero">
          <div className="apply-hero-icon"><Icon size={26} /></div>
          <h1 className="apply-title">{cfg.title}</h1>
          <p className="apply-lead">{cfg.lead}</p>
        </header>

        {gated ? (
          <div className="apply-gated" data-testid="apply-gated">
            <p>{gated.reason}</p>
            <button className="btn-solid" onClick={() => navigate(gated.cta)}>Go to sign in</button>
          </div>
        ) : submitted ? (
          <div className="apply-done" data-testid="apply-done">
            <div className="apply-done-check"><Check size={28} /></div>
            <h2>You're through.</h2>
            <p>An admin will review your application and email you at <b>{form.contact_email}</b>.</p>
            <button className="btn-solid" onClick={() => navigate("/members")}>Back to Members</button>
          </div>
        ) : (
          <form className="apply-form" onSubmit={submit}>
            <div className="apply-row">
              <label className="auth-field">
                <span>{cfg.labels.display_name}*</span>
                <input value={form.display_name} onChange={set("display_name")} data-testid="apply-display-name" required />
              </label>
              <label className="auth-field">
                <span>Contact email*</span>
                <input type="email" value={form.contact_email} onChange={set("contact_email")} data-testid="apply-email" required />
              </label>
            </div>
            <label className="auth-field">
              <span>{cfg.labels.tagline}</span>
              <input value={form.tagline} onChange={set("tagline")} data-testid="apply-tagline" />
            </label>
            <label className="auth-field">
              <span>Your pitch*</span>
              <textarea rows={6} value={form.pitch} onChange={set("pitch")} data-testid="apply-pitch" required />
            </label>
            <div className="apply-row">
              <label className="auth-field">
                <span>Instagram</span>
                <input value={form.instagram} onChange={set("instagram")} placeholder="@handle" data-testid="apply-instagram" />
              </label>
              <label className="auth-field">
                <span>Website</span>
                <input value={form.website} onChange={set("website")} placeholder="https://…" data-testid="apply-website" />
              </label>
            </div>
            <div className="apply-row">
              <label className="auth-field">
                <span>Other socials</span>
                <input value={form.socials} onChange={set("socials")} placeholder="TikTok / SoundCloud / Twitter…" />
              </label>
              <label className="auth-field">
                <span>Location</span>
                <input value={form.location} onChange={set("location")} placeholder="City, Country" />
              </label>
            </div>

            <div className="apply-uploads">
              <div className="apply-upload-block">
                <div className="apply-upload-label">Cover / hero image</div>
                <label className="btn-ghost apply-upload-btn" data-testid="apply-hero-upload">
                  <Upload size={16} /> {hero ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" hidden onChange={(e) => onFile(e, "hero")} />
                </label>
                {hero && (
                  <div className="apply-hero-preview" data-testid="apply-hero-preview">
                    <img src={hero.url} alt="hero" />
                    <button type="button" className="chip-x" onClick={() => setHero(null)} aria-label="remove hero">
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div className="apply-upload-block">
                <div className="apply-upload-label">
                  Media {kind === "music" ? "(demos + press photos)" : kind === "art" ? "(pieces)" : "(product / lookbook)"}
                </div>
                <label className="btn-ghost apply-upload-btn" data-testid="apply-media-upload">
                  <Upload size={16} /> Add files
                  <input type="file" accept={cfg.accept} multiple hidden onChange={(e) => onFile(e, "media")} />
                </label>
                <div className="apply-media-grid">
                  {media.map((m, idx) => (
                    <div key={idx} className={`apply-media-tile media-${m.kind}`} data-testid={`apply-media-tile-${idx}`}>
                      {m.kind === "image" ? (
                        <img src={m.url} alt="" />
                      ) : m.kind === "audio" ? (
                        <audio src={m.url} controls preload="none" />
                      ) : m.kind === "video" ? (
                        <video src={m.url} controls preload="none" />
                      ) : (
                        <span className="apply-media-file">{m.original_filename || "file"}</span>
                      )}
                      <button type="button" className="chip-x" onClick={() => removeMedia(idx)} aria-label="remove file">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {uploadErr && <div className="auth-error" data-testid="apply-upload-error">{uploadErr}</div>}
            {err && <div className="auth-error" data-testid="apply-error">{err}</div>}

            <button type="submit" className="btn-solid btn-huge" disabled={busy} data-testid="apply-submit">
              {busy ? "…" : "Submit application"}
            </button>
          </form>
        )}
      </div>
    </>
  );
};

export default Apply;
